/**
 * ALOHA QUEEN SUPERNOVA
 * Admin Test User Management
 * Copyright © 2025 Malcolm KH Lee III. All Rights Reserved.
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Users, Plus, Trash2, Key, Crown } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminTestUsers = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [testUsers, setTestUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  
  // Form state
  const [newUser, setNewUser] = useState({
    email: '',
    full_name: '',
    tier: 'PRO',
    password: 'test123'
  });

  useEffect(() => {
    if (!user || user.subscription_tier !== 'LIFETIME_ENTERPRISE') {
      navigate('/');
      return;
    }
    fetchTestUsers();
  }, [user]);

  const fetchTestUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/test-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTestUsers(response.data.test_users || []);
    } catch (error) {
      console.error('Failed to fetch test users:', error);
    } finally {
      setLoading(false);
    }
  };

  const createTestUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    
    try {
      const response = await axios.post(
        `${API}/admin/test-users/create`,
        newUser,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert(`✅ Test user created!\n\nEmail: ${response.data.user.email}\nPassword: ${response.data.user.password}\n\nShare these credentials with your test team.`);
      
      setNewUser({ email: '', full_name: '', tier: 'PRO', password: 'test123' });
      setShowCreateForm(false);
      fetchTestUsers();
    } catch (error) {
      alert(`❌ Error: ${error.response?.data?.detail || 'Failed to create user'}`);
    } finally {
      setCreating(false);
    }
  };

  const deleteTestUser = async (userId, email) => {
    if (!window.confirm(`Delete test user ${email}?`)) return;
    
    try {
      await axios.delete(`${API}/admin/test-users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('✅ Test user deleted');
      fetchTestUsers();
    } catch (error) {
      alert(`❌ Error: ${error.response?.data?.detail || 'Failed to delete user'}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-purple-900/20 to-gray-900 py-12 px-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Crown className="w-8 h-8 text-yellow-400" />
            <h1 className="text-4xl font-bold text-white">Test User Management</h1>
          </div>
          <p className="text-gray-300">Create and manage test accounts for your QA team</p>
        </div>

        {/* Create Button */}
        <div className="mb-6">
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition"
          >
            <Plus className="w-5 h-5" />
            Create Test User
          </button>
        </div>

        {/* Create Form */}
        {showCreateForm && (
          <div className="bg-gray-800/50 backdrop-blur-md p-6 rounded-xl mb-6 border border-green-500/30">
            <h2 className="text-xl font-bold text-white mb-4">New Test User</h2>
            <form onSubmit={createTestUser} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white mb-2">Email</label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                    placeholder="tester@example.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-white mb-2">Full Name</label>
                  <input
                    type="text"
                    value={newUser.full_name}
                    onChange={(e) => setNewUser({...newUser, full_name: e.target.value})}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                    placeholder="Test User 1"
                    required
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white mb-2">Subscription Tier</label>
                  <select
                    value={newUser.tier}
                    onChange={(e) => setNewUser({...newUser, tier: e.target.value})}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                  >
                    <option value="FREE">FREE (10 prompts)</option>
                    <option value="BASIC">BASIC ($9.99/mo)</option>
                    <option value="PRO">PRO ($49.99/mo) - Recommended</option>
                    <option value="ENTERPRISE">ENTERPRISE ($99.99/mo)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-white mb-2">Password</label>
                  <input
                    type="text"
                    value={newUser.password}
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                    placeholder="test123"
                    required
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={creating}
                  className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition disabled:opacity-50"
                >
                  {creating ? 'Creating...' : 'Create Test User'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-lg transition"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Test Users List */}
        <div className="bg-gray-800/50 backdrop-blur-md rounded-xl border border-purple-500/30 overflow-hidden">
          <div className="p-6 border-b border-gray-700">
            <div className="flex items-center gap-2">
              <Users className="w-6 h-6 text-purple-400" />
              <h2 className="text-2xl font-bold text-white">
                Test Users ({testUsers.length})
              </h2>
            </div>
          </div>

          {testUsers.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">No test users created yet</p>
              <p className="text-gray-500 text-sm">Create test accounts for your QA team</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-700">
              {testUsers.map((testUser) => (
                <div key={testUser.id} className="p-4 hover:bg-gray-700/30 transition">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-white">{testUser.full_name}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          testUser.subscription_tier === 'ENTERPRISE' ? 'bg-purple-500/30 text-purple-300' :
                          testUser.subscription_tier === 'PRO' ? 'bg-blue-500/30 text-blue-300' :
                          testUser.subscription_tier === 'BASIC' ? 'bg-green-500/30 text-green-300' :
                          'bg-gray-500/30 text-gray-300'
                        }`}>
                          {testUser.subscription_tier}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-400 text-sm">
                        <Key className="w-4 h-4" />
                        <span>{testUser.email}</span>
                      </div>
                      <div className="text-gray-500 text-xs mt-1">
                        Created: {new Date(testUser.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    
                    <button
                      onClick={() => deleteTestUser(testUser.id, testUser.email)}
                      className="p-2 bg-red-600/20 hover:bg-red-600/40 text-red-400 rounded-lg transition"
                      title="Delete test user"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="mt-6 bg-blue-900/30 border border-blue-500/30 rounded-lg p-4">
          <h3 className="text-white font-bold mb-2">💡 Instructions for Your Test Team:</h3>
          <ol className="text-gray-300 text-sm space-y-1 list-decimal list-inside">
            <li>Create test users with desired subscription tiers</li>
            <li>Share the email + password with your QA team</li>
            <li>Test team can login at: <a href="/login" className="text-blue-400 hover:underline">Login Page</a></li>
            <li>Test all features based on their tier level</li>
            <li>Delete test users when testing is complete</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default AdminTestUsers;
